import type { ReactNode } from "react";

import { PROGRAM_STATUS_LABELS } from "@/access/programStatus";
import { ACCESS_TRACKS } from "@/access/tracks";
import { useListUserPrograms } from "@/api/generated";
import type {
  ProgramStatus,
  UserProgramResponse,
} from "@/api/generated/models";
import {
  H3,
  Header,
  Paragraph,
  ScrollView,
  Section,
  Separator,
  Skeleton,
  Text,
  XStack,
  YStack,
  useMedia,
} from "@/components";

export default function ProgramsSettingsScreen() {
  const { lg } = useMedia();
  const { data, isLoading, isError } = useListUserPrograms();

  const programs = data ?? [];

  return (
    <Section flex={1} $lg={{ flex: 1, gap: "$250" }}>
      {lg && (
        <Header
          marginHorizontal="$screenPadding"
          marginTop="$screenPadding"
          gap="$250"
        >
          <H3>Programs</H3>
          <Separator borderColor="$fillNeutral" />
        </Header>
      )}
      <ScrollView
        contentContainerStyle={{
          margin: "$screenPadding",
          marginBottom: "$1000",
          ...(lg && { marginTop: "$50" }),
        }}
      >
        {isLoading ? (
          <YStack gap="$150">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton height="$800" key={i} />
            ))}
          </YStack>
        ) : isError ? (
          <Paragraph role="alert">
            We couldn't load your programs. Please try again later.
          </Paragraph>
        ) : programs.length === 0 ? (
          <Paragraph>You are not enrolled in any programs.</Paragraph>
        ) : (
          <YStack gap="$150">
            {programs.map((program) => (
              <ProgramCard key={program.program} program={program} />
            ))}
          </YStack>
        )}
      </ScrollView>
    </Section>
  );
}

function ProgramCard({ program }: { program: UserProgramResponse }) {
  const track = ACCESS_TRACKS[program.program];
  return (
    <YStack
      gap="$100"
      borderWidth="$25"
      borderColor="$fillNeutral"
      borderRadius="$150"
      padding="$200"
    >
      <Field
        label={
          <YStack gap="$50">
            <Text size="$sm" bold>
              {track.groupLabel}
            </Text>
            <Text size="$sm">{track.label}</Text>
          </YStack>
        }
        value={<StatusPill status={program.status} />}
      />
    </YStack>
  );
}

function Field({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <XStack justifyContent="space-between" alignItems="flex-start" gap="$100">
      <YStack flex={1} minWidth={0}>
        {label}
      </YStack>
      <YStack flexShrink={0}>{value}</YStack>
    </XStack>
  );
}

function StatusPill({ status }: { status: ProgramStatus }) {
  const active = status === "enrolled";
  return (
    <Text
      size="$xs"
      color={active ? "$textPrimary" : "$textSecondary"}
      backgroundColor={active ? "$fillBadgeCurai" : "$fillNeutral"}
      paddingHorizontal="$100"
      paddingVertical="$25"
      borderRadius="$100"
    >
      {active && "✓ "}
      {PROGRAM_STATUS_LABELS[status]}
    </Text>
  );
}
